'use client';

import Image from 'next/image';
import { MapPinIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { trackEvent } from '../../../lib/analytics/posthog';
import { EVENTS } from '../../../lib/analytics/events';
import { formatPhoneDisplay, telHref, whatsappUrl } from '@utils/phone';
import { vendorStorefrontUrl } from '@utils/storefront-url';
import { formatRating, profileRatingLabel } from '@lib/vendor-rating';

const WHATSAPP_ICON_PATH =
  'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z';

function RatingRow({ rating, href, external = false }) {
  const content = (
    <>
      <StarIcon className="h-4 w-4 text-amber-400" aria-hidden="true" />
      <span className="font-semibold text-[#1a1a1a] tabular-nums">{formatRating(rating.rating)}</span>
      <span className="text-gray-500">· {profileRatingLabel(rating)}</span>
    </>
  );
  const className = 'inline-flex items-center gap-1.5 text-[13px]';
  if (!href) return <span className={className}>{content}</span>;
  return (
    <a
      href={href}
      {...(external && { target: '_blank', rel: 'noopener noreferrer' })}
      className={`${className} rounded-md px-1 hover:underline decoration-gray-300 underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500`}
    >
      {content}
    </a>
  );
}

/**
 * Tarjeta lateral del negocio (perfil y detalle de servicio). Los CTA son links con forma de botón
 * (nunca un <button> dentro de un <a>). Con `service`, «Ver turnos disponibles» lleva a la reserva de
 * ese servicio en la vidriera; sin él, a la vidriera del negocio. `ratings` = { agora, google } de
 * lib/vendor-rating.js: las dos calificaciones, cada una con su fuente (nunca un número combinado).
 */
export default function AvatarVendorCard({ vendor, service = null, campaign = 'vendor_profile', ratings = null }) {
  const location = vendor.services?.[0]?.address || null;
  const vendorCategory = vendor.services?.[0]?.category_sub_category?.category?.name;
  const name = vendor.vendor_storefront?.name || `${vendor.user.first_name} ${vendor.user.last_name}`.trim();

  // El teléfono del negocio es el que cargó como WhatsApp: sin 9 ni 15 se toma igual como celular.
  const waHref = whatsappUrl(vendor.user?.phone, { assumeMobile: true, country: vendor.country });
  const phoneHref = telHref(vendor.user?.phone, { assumeMobile: true, country: vendor.country });
  const bookingHref = vendor.is_created_by_marketplace
    ? null
    : vendorStorefrontUrl(vendor.username, { serviceSlug: service?.slug, campaign, content: 'booking_cta' });
  const mapsHref = location
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`
    : null;

  const trackWhatsApp = () =>
    trackEvent(EVENTS.VENDOR_CONTACT_CLICKED, {
      vendor_username: vendor.username,
      contact_type: 'whatsapp',
      destination: 'whatsapp',
      vendor_category: vendorCategory
    });
  const trackBooking = () =>
    trackEvent(EVENTS.VENDOR_CONTACT_CLICKED, {
      vendor_username: vendor.username,
      contact_type: 'booking_cta',
      destination: 'agora_storefront',
      vendor_category: vendorCategory,
      ...(service && { service_slug: service.slug })
    });

  const primaryClass =
    'flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-[13px] font-medium text-white transition-all duration-200 active:scale-[0.98] sm:py-3 sm:text-[14px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';

  return (
    <div className="w-full md:sticky md:top-[88px] animate-landing-fade-up">
      <div className="rounded-2xl bg-white border border-gray-100 overflow-hidden shadow-sm">
        {/* Header gradient strip */}
        <div className="h-20 bg-gradient-to-r from-primary-500/10 via-primary-400/5 to-transparent" />

        {/* Avatar — overlapping the gradient */}
        <div className="flex flex-col items-center -mt-12 px-5 pb-5">
          <div className="relative">
            <div className="w-[88px] h-[88px] rounded-full overflow-hidden ring-4 ring-white shadow-md">
              <Image
                src={vendor.user.avatar_url}
                alt={`Logo de ${name}`}
                width={88}
                height={88}
                className="object-cover w-full h-full"
              />
            </div>
          </div>

          <h2 className="text-lg font-bold text-[#1a1a1a] mt-3 tracking-[-0.01em] text-center">{name}</h2>

          {vendor.professional_title && (
            <span className="text-[12px] font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full mt-2 text-center">
              {vendor.professional_title}
            </span>
          )}

          {(ratings?.agora || ratings?.google) && (
            <div className="mt-3 flex flex-col items-center gap-1">
              {ratings.agora && (
                <RatingRow
                  rating={ratings.agora}
                  href={service ? `/negocios/${vendor.username}#resenas-verificadas` : '#resenas-verificadas'}
                />
              )}
              {ratings.google && <RatingRow rating={ratings.google} href={ratings.google.href} external />}
            </div>
          )}

          {/* Datos de contacto: toda la fila es el link */}
          {(location || phoneHref) && (
            <div className="hidden sm:block w-full mt-5 space-y-1">
              {location && (
                <a
                  href={mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <MapPinIcon className="h-[18px] w-[18px] text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-[13px] text-gray-600 leading-snug">{location}</span>
                </a>
              )}
              {phoneHref && (
                <a
                  href={phoneHref}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <PhoneIcon className="h-[18px] w-[18px] text-gray-400 flex-shrink-0" />
                  <span className="text-[13px] text-gray-600 tabular-nums">
                    {formatPhoneDisplay(vendor.user.phone, { assumeMobile: true, country: vendor.country })}
                  </span>
                </a>
              )}
            </div>
          )}

          {/* CTA */}
          {(bookingHref || waHref) && (
            <div className="w-full mt-4 sm:mt-5 space-y-2">
              {bookingHref && (
                <a
                  href={bookingHref}
                  onClick={trackBooking}
                  className={`${primaryClass} bg-primary-500 hover:bg-primary-600 shadow-sm shadow-primary-500/20 focus-visible:outline-primary-600`}
                >
                  Ver turnos disponibles
                </a>
              )}
              {waHref &&
                (bookingHref ? (
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackWhatsApp}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 py-2.5 text-[13px] font-medium text-gray-600 transition-all duration-200 hover:bg-gray-50 active:scale-[0.98]"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 text-emerald-600" aria-hidden="true">
                      <path d={WHATSAPP_ICON_PATH} />
                    </svg>
                    Enviar mensaje por WhatsApp
                  </a>
                ) : (
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackWhatsApp}
                    className={`${primaryClass} bg-emerald-500 hover:bg-emerald-600 shadow-sm shadow-emerald-500/20 focus-visible:outline-emerald-600`}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                      <path d={WHATSAPP_ICON_PATH} />
                    </svg>
                    Contactar por WhatsApp
                  </a>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
